import { Link, Outlet, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, describeError, RequestError } from "../../lib/api";
import { Avatar, EmptyState, Spinner } from "../../components/ui";
import type { Club, ClubSummary } from "@pagemates/shared";
import { PublicClubPage } from "../../components/PublicClub";
import { Bell } from "../../components/Bell";
import { BackIcon, CalendarIcon, CheckIcon, ClubIcon, LampIcon, ShelfIcon, SwapIcon, TalkIcon } from "../../components/Icons";
import { useClub as useClubQuery, useMe } from "../../lib/queries";
import { club } from "@pagemates/shared/copy";

const txt = club.layout;

export function useClub() {
  const { clubId } = useParams({ from: "/authed/clubs/$clubId" });
  // A 404 is the answer (private, wrong link or left), not a hiccup: don't retry it.
  const club = useClubQuery(clubId, { retry: (n, e) => !(e instanceof RequestError && e.status === 404) && n < 3 });
  return { clubId, club };
}

/** Links by id (or an old slug) are rewritten to the club's current slug, keeping the page and search. */
function useCanonicalSlug(clubId: string, slug: string | undefined) {
  const navigate = useNavigate();
  useEffect(() => {
    if (slug && slug !== clubId) void navigate({ to: ".", params: (p: Record<string, string>) => ({ ...p, clubId: slug }), search: (s: Record<string, unknown>) => s, replace: true });
  }, [clubId, slug, navigate]);
}

const TABS = [
  { to: "/clubs/$clubId", label: txt.reading, exact: true, Icon: LampIcon },
  { to: "/clubs/$clubId/talk", label: txt.talk, exact: false, Icon: TalkIcon },
  { to: "/clubs/$clubId/shelf", label: txt.shelf, exact: false, Icon: ShelfIcon },
  { to: "/clubs/$clubId/meeting", label: txt.meeting, exact: false, Icon: CalendarIcon },
  { to: "/clubs/$clubId/club", label: txt.club, exact: false, Icon: ClubIcon },
] as const;

/** The Meeting tab gets a dot when the meeting is within a week and you haven't answered, or the date moved. */
function meetingNeedsYou(club: Club | undefined): boolean {
  const m = club?.meeting;
  if (!m) return false;
  const at = new Date(m.at).getTime();
  if (at < Date.now()) return false;
  if (m.rsvp.mine?.stale) return true;
  return !m.rsvp.mine && at - Date.now() < 7 * 86_400_000;
}

// Club shell. Phone: title bar on top, tabs fixed at the bottom. Desktop
// (≥ 900px): a sidebar with the sections, a club switcher and your profile.
export function ClubLayout() {
  const { clubId, club } = useClub();
  const me = useMe();
  const navigate = useNavigate();
  const myName = me.data ? me.data.displayName ?? me.data.name : "";
  useCanonicalSlug(clubId, club.data?.slug);
  const dot = meetingNeedsYou(club.data);
  const waiting = (club.data?.pendingRequests ?? 0) > 0; // people asking to join: the Club tab's dot, for owners and admins
  const board = club.data?.board;
  const boardNew = Boolean(board && board.newPosts + board.newReplies > 0); // new on the noticeboard since your last visit: the same dot
  const clubDot = waiting || boardNew;
  const clubDotLabel = waiting ? txt.peopleAreAskingTo : txt.newOnTheNoticeboard;

  // A club you can't see: no sidebar or tabs, nothing about it. The same page for a private club and a wrong link.
  if (club.isError && club.error instanceof RequestError && club.error.status === 404) return <NotAMember clubId={clubId} former={club.error.code === "former_member"} />;

  return (
    <div className="shell">
      <aside className="side">
        <div className="side-club">
          {me.data && me.data.clubs.length > 1
            ? <ClubSwitcher name={club.data?.name ?? ""} clubs={me.data.clubs} currentId={club.data?.id} />
            : <span className="t">{club.data?.name ?? " "}</span>}
        </div>
        <nav className="side-nav" aria-label={txt.clubSections}>
          {TABS.map(({ to, label, exact, Icon }) => (
            <Link key={label} to={to} params={{ clubId }} activeOptions={{ exact }} className="nav"><Icon />{label}{label === "Meeting" && dot && <i className="dot" aria-label={txt.needsYourAnswer} />}{label === "Club" && clubDot && <i className="dot" aria-label={clubDotLabel} />}</Link>
          ))}
        </nav>
        <Bell nav />
        <Link to="/clubs" search={{ all: true }} activeOptions={{ exact: true }} className="nav quiet"><BackIcon />{txt.allClubs}</Link>
        {me.data && <Link to="/me" className="side-me"><Avatar name={myName} image={me.data.image} /><span>{myName}</span></Link>}
      </aside>

      <div className="main">
        <header className="topbar mobile-only">
          <div className="row grow">
            {/* The shell's arrow always goes to all clubs; pages inside a club carry their own BackBar. */}
            <Link to="/clubs" search={{ all: true }} className="back" aria-label={txt.allClubs}><BackIcon /></Link>
            <span className="title">{club.data?.name ?? " "}</span>
          </div>
          <div className="row" style={{ gap: 6 }}>
            <Bell />
            {me.data && <Link to="/me" className="row" aria-label={txt.yourProfile}><Avatar name={myName} image={me.data.image} /></Link>}
          </div>
        </header>
        {club.isPending && <main className="page"><Spinner /></main>}
        {club.isError && <main className="page"><p className="error">{describeError(club.error)}</p></main>}
        {club.data && <Outlet />}
        <nav className="tabbar mobile-only" aria-label={txt.clubSections}>
          {TABS.map(({ to, label, exact, Icon }) => (
            <Link key={label} to={to} params={{ clubId }} activeOptions={{ exact }}><span className="ico"><Icon />{label === "Meeting" && dot && <i className="dot" aria-label={txt.needsYourAnswer} />}{label === "Club" && clubDot && <i className="dot" aria-label={clubDotLabel} />}</span>{label}</Link>
          ))}
        </nav>
      </div>
    </div>
  );
}

/**
 * The sidebar's club switcher: the club's name is the button; the menu lists
 * your clubs with their current book, unread Talk and a check on this one,
 * then "All clubs". Arrow keys move, Enter opens, Escape or a click outside closes.
 */
function ClubSwitcher({ name, clubs, currentId }: { name: string; clubs: ClubSummary[]; currentId?: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const byKey = useRef(false); // opened from the keyboard: focus the list; a click leaves focus on the button
  const ref = useRef<HTMLDivElement>(null);
  const items = useRef<(HTMLElement | null)[]>([]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    const here = clubs.findIndex((c) => c.id === currentId);
    if (byKey.current) requestAnimationFrame(() => items.current[Math.max(0, here)]?.focus());
    return () => document.removeEventListener("mousedown", close);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = (slug: string) => { setOpen(false); void navigate({ to: "/clubs/$clubId", params: { clubId: slug } }); };
  const keys = (e: React.KeyboardEvent) => {
    const list = items.current.filter(Boolean) as HTMLElement[];
    const i = list.indexOf(document.activeElement as HTMLElement); // -1 on the button: ArrowDown lands on the first club
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); ref.current?.querySelector<HTMLButtonElement>(".cs-btn")?.focus(); }
    if (e.key === "ArrowDown") { e.preventDefault(); list[(i + 1) % list.length]?.focus(); }
    if (e.key === "ArrowUp") { e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); }
  };
  return (
    <div className="club-switcher" ref={ref} onKeyDown={keys}>
      <button type="button" className="cs-btn" aria-haspopup="menu" aria-expanded={open} onClick={(e) => { byKey.current = e.detail === 0; setOpen((o) => !o); }}
        onKeyDown={(e) => { if (e.key === "ArrowDown" && !open) { e.preventDefault(); byKey.current = true; setOpen(true); } }}>
        <span className="t">{name}</span><SwapIcon width={16} height={16} aria-hidden="true" />
      </button>
      {open && (
        <div className="menu cs-menu" role="menu" aria-label={txt.switchClub}>
          {clubs.map((c, i) => {
            const b = c.currentBook;
            const here = c.id === currentId;
            return (
              <button key={c.id} ref={(el) => { items.current[i] = el; }} role="menuitemradio" aria-checked={here} className={`cs-item${here ? " here" : ""}`} onClick={() => go(c.slug)}>
                <span className="cs-cover">{b?.coverUrl ? <img src={b.coverUrl} alt="" /> : null}</span>
                <span className="cs-text">
                  <span className="cs-name">{c.name}</span>
                  <span className="cs-sub">{b ? `${b.title} · ${b.myProgress >= 100 ? txt.finished : `${b.myProgress}%`}` : txt.nothingOnTheNightstand}</span>
                </span>
                {c.unreadMessages > 0 && !here && <span className="cs-new" aria-label={txt.newMessages(c.unreadMessages)}>{c.unreadMessages}</span>}
                {here && <CheckIcon className="cs-check" width={16} height={16} aria-hidden="true" />}
              </button>
            );
          })}
          <hr className="cs-rule" />
          <button ref={(el) => { items.current[clubs.length] = el; }} role="menuitem" className="cs-all" onClick={() => { setOpen(false); void navigate({ to: "/clubs", search: { all: true } }); }}>{txt.allClubs}</button>
        </div>
      )}
    </div>
  );
}

/**
 * The page for a club you can't open. Private or wrong link: one message,
 * nothing from the club (the server answers 404 for both). Former members
 * already know the club exists, so they're told they've left it.
 */
/** Not a member: a public club's page if it has one (former members can come back through it), otherwise the private page. */
function NotAMember({ clubId, former }: { clubId: string; former: boolean }) {
  const pub = useQuery({ queryKey: ["public-club", clubId], queryFn: () => api.publicClub(clubId), retry: false });
  if (pub.isPending) return <main className="page"><Spinner /></main>;
  if (pub.data) return <PublicClubPage clubId={clubId} />;
  return <ClubGone former={former} />;
}

function ClubGone({ former }: { former: boolean }) {
  return (
    <main className="page club-gone">
      <EmptyState title={former ? txt.youReNoLonger : txt.thisClubIsPrivate}
        action={<Link to="/clubs/browse" className="btn primary">{txt.findAClub}</Link>}
        secondary={<Link to="/clubs" search={{ all: true }}>{txt.goToYourClubs}</Link>}>
        {former ? txt.toComeBackAsk : txt.askAMemberFor}
      </EmptyState>
    </main>
  );
}
